// Table.jsx
import React, { useState, useEffect, useMemo } from 'react';
import './Table.css';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';

const Table = ({ data }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [selectedRows, setSelectedRows] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [activeTab, setActiveTab] = useState('orders');
    const [showFilterPanel, setShowFilterPanel] = useState(false);

    // Фильтры по типам
    const [filters, setFilters] = useState({
        orders: true,
        operations: true,
        resources: true
    });

    // Сбор всех данных в плоский список с сохранением связей
    const allItems = useMemo(() => {
        if (!data) return [];

        const items = [];

        data.forEach(order => {
            // Добавляем заказ
            const orderItem = {
                id: order.id,
                type: 'Заказ',
                name: order.name || order.orderNumber,
                number: order.orderNumber,
                startDate: order.startDate,
                endDate: order.endDate,
                status: order.status,
                description: order.description,
                parentId: null,
                level: 0,
                originalOrderId: order.id,
                originalOperationId: null,
                hasChildren: order.operations && order.operations.length > 0
            };
            items.push(orderItem);

            // Добавляем операции заказа
            if (order.operations) {
                order.operations.forEach(operation => {
                    const operationItem = {
                        id: `${order.id}-${operation.id}`,
                        type: 'Операция',
                        name: operation.name || operation.operationNumber,
                        number: operation.operationNumber,
                        startDate: operation.startDate,
                        endDate: operation.endDate,
                        status: operation.status,
                        description: operation.description,
                        parentId: order.id,
                        level: 1,
                        originalOrderId: order.id,
                        originalOperationId: operation.id,
                        hasChildren: operation.resources && operation.resources.length > 0
                    };
                    items.push(operationItem);

                    // Добавляем ресурсы операции
                    if (operation.resources) {
                        operation.resources.forEach(resource => {
                            items.push({
                                id: `${order.id}-${operation.id}-${resource.id}`,
                                type: 'Ресурс',
                                name: resource.name || resource.agentName,
                                number: resource.agentId,
                                startDate: resource.startDate,
                                endDate: resource.endDate,
                                status: resource.status,
                                description: resource.type,
                                parentId: `${order.id}-${operation.id}`,
                                level: 2,
                                originalOrderId: order.id,
                                originalOperationId: operation.id,
                                hasChildren: false
                            });
                        });
                    }
                });
            }
        });

        return items;
    }, [data]);

    // Функция для фильтрации данных с учетом иерархии
    const filterItemsWithHierarchy = useMemo(() => {
        if (!allItems.length) return [];

        // Сначала собираем все данные
        let filtered = [...allItems];

        // Фильтрация по типу с учетом иерархии
        if (!filters.operations || !filters.resources) {
            const visibleItems = new Set();

            // Всегда показываем заказы если они включены
            if (filters.orders) {
                allItems
                    .filter(item => item.type === 'Заказ')
                    .forEach(order => visibleItems.add(order.id));
            }

            // Показываем операции только если они включены И их заказ виден
            if (filters.operations) {
                allItems
                    .filter(item => item.type === 'Операция')
                    .forEach(operation => {
                        if (visibleItems.has(operation.parentId)) {
                            visibleItems.add(operation.id);
                        }
                    });
            }

            // Показываем ресурсы только если они включены И их операция видна
            if (filters.resources) {
                allItems
                    .filter(item => item.type === 'Ресурс')
                    .forEach(resource => {
                        const parentOperation = allItems.find(item =>
                            item.type === 'Операция' && item.id === resource.parentId
                        );
                        if (parentOperation && visibleItems.has(parentOperation.id)) {
                            visibleItems.add(resource.id);
                        }
                    });
            }

            filtered = filtered.filter(item => visibleItems.has(item.id));
        }

        // Поиск по тексту
        if (searchTerm.trim()) {
            const searchLower = searchTerm.toLowerCase();
            filtered = filtered.filter(item =>
                (item.name && item.name.toLowerCase().includes(searchLower)) ||
                (item.number && item.number.toString().toLowerCase().includes(searchLower)) ||
                (item.description && item.description.toLowerCase().includes(searchLower)) ||
                (item.type && item.type.toLowerCase().includes(searchLower))
            );
        }

        return filtered;
    }, [allItems, filters, searchTerm]);

    // Сортировка данных
    const sortedData = useMemo(() => {
        if (!sortConfig.key) return filterItemsWithHierarchy;

        const sorted = [...filterItemsWithHierarchy].sort((a, b) => {
            let aValue = a[sortConfig.key];
            let bValue = b[sortConfig.key];

            // Преобразование дат для сортировки
            if (sortConfig.key.includes('Date') && aValue && bValue) {
                aValue = new Date(aValue).getTime();
                bValue = new Date(bValue).getTime();
            }

            // Сравнение значений
            if (aValue < bValue) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
        });

        return sorted;
    }, [filterItemsWithHierarchy, sortConfig]);

    // Обработчик переключения фильтров
    const handleFilterToggle = (filterType) => {
        setFilters(prev => {
            const newFilters = { ...prev };

            // Заказы всегда должны быть видимы
            if (filterType === 'orders') {
                newFilters.orders = true; // Заказы всегда включены
            } else if (filterType === 'operations') {
                newFilters.operations = !prev.operations;
                // Если выключаем операции, то и ресурсы тоже выключаем
                if (!newFilters.operations) {
                    newFilters.resources = false;
                }
            } else if (filterType === 'resources') {
                newFilters.resources = !prev.resources;
                // Если включаем ресурсы, то и операции тоже включаем
                if (newFilters.resources && !newFilters.operations) {
                    newFilters.operations = true;
                }
            }

            return newFilters;
        });
    };

    // Сброс всех фильтров
    const handleResetFilters = () => {
        setFilters({
            orders: true,
            operations: true,
            resources: true
        });
        setSearchTerm('');
    };

    // Обработчик сортировки
    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    // Обработчик выбора строки
    const handleRowSelect = (id) => {
        setSelectedRows(prev =>
            prev.includes(id)
                ? prev.filter(rowId => rowId !== id)
                : [...prev, id]
        );
    };

    // Обработчик выбора всех строк
    const handleSelectAll = () => {
        if (selectedRows.length === sortedData.length) {
            setSelectedRows([]);
        } else {
            setSelectedRows(sortedData.map(item => item.id));
        }
    };

    // Форматирование даты
    const formatDate = (dateString) => {
        if (!dateString) return '—';
        const date = new Date(dateString);
        return date.toLocaleDateString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    // Форматирование статуса
    const getStatusInfo = (status) => {
        const statusMap = {
            1: { text: 'Утверждено', color: '#10b981', bgColor: '#d1fae5' },
            2: { text: 'Завершено', color: '#6b7280', bgColor: '#f3f4f6' },
            3: { text: 'Забронировано', color: '#f59e0b', bgColor: '#fef3c7' },
            4: { text: 'Отменено', color: '#ef4444', bgColor: '#fee2e2' },
            5: { text: 'Готов к выполнению', color: '#eab308', bgColor: '#fef9c3' },
            6: { text: 'В работе', color: '#3b82f6', bgColor: '#dbeafe' },
            7: { text: 'На рассмотрении', color: '#8b5cf6', bgColor: '#ede9fe' },
            10: { text: 'Утверждено', color: '#10b981', bgColor: '#d1fae5' }
        };

        return statusMap[status] || { text: 'Неизвестно', color: '#6b7280', bgColor: '#f3f4f6' };
    };

    // Получение иконки для типа
    const getTypeIcon = (type) => {
        switch (type) {
            case 'Заказ': return '📋';
            case 'Операция': return '⚙️';
            case 'Ресурс': return '👤';
            default: return '📄';
        }
    };

    // Получение цвета для типа
    const getTypeColor = (type) => {
        switch (type) {
            case 'Заказ': return '#667eea';
            case 'Операция': return '#f093fb';
            case 'Ресурс': return '#4facfe';
            default: return '#6b7280';
        }
    };

    // Статистика по типам
    const typeStats = useMemo(() => {
        const stats = {
            orders: 0,
            operations: 0,
            resources: 0
        };

        allItems.forEach(item => {
            if (item.type === 'Заказ') stats.orders++;
            else if (item.type === 'Операция') stats.operations++;
            else if (item.type === 'Ресурс') stats.resources++;
        });

        return stats;
    }, [allItems]);

    // Статистика видимых элементов
    const visibleStats = useMemo(() => {
        const stats = {
            orders: 0,
            operations: 0,
            resources: 0
        };

        filterItemsWithHierarchy.forEach(item => {
            if (item.type === 'Заказ') stats.orders++;
            else if (item.type === 'Операция') stats.operations++;
            else if (item.type === 'Ресурс') stats.resources++;
        });

        return stats;
    }, [filterItemsWithHierarchy]);

    return (
        <div className="ntb-table-container">
            {/* Панель фильтров */}
            {showFilterPanel && (
                <div className="ntb-filter-panel-overlay" onClick={() => setShowFilterPanel(false)}>
                    <div className="ntb-filter-panel" onClick={(e) => e.stopPropagation()}>
                        <div className="ntb-filter-panel-header">
                            <h3 className="ntb-filter-panel-title">
                                <FilterListIcon className="ntb-filter-icon" />
                                Фильтры
                            </h3>
                            <button
                                className="ntb-filter-panel-close"
                                onClick={() => setShowFilterPanel(false)}
                            >
                                <ClearIcon />
                            </button>
                        </div>

                        <div className="ntb-filter-options">
                            <div className="ntb-filter-section">
                                <h4 className="ntb-filter-section-title">Типы элементов</h4>
                                <div className="ntb-filter-checkboxes">
                                    <div className="ntb-filter-checkbox-group">
                                        <div className="ntb-filter-checkbox-item">
                                            <label className="ntb-filter-checkbox-label">
                                                <input
                                                    type="checkbox"
                                                    checked={filters.orders}
                                                    disabled={true}
                                                    onChange={() => handleFilterToggle('orders')}
                                                    className="ntb-filter-checkbox-input"
                                                />
                                                <span className={`ntb-filter-checkbox-custom ${filters.orders ? 'checked' : ''}`}>
                                                    {filters.orders ? <CheckBoxIcon /> : <CheckBoxOutlineBlankIcon />}
                                                </span>
                                                <span className="ntb-filter-checkbox-text">
                                                    <span className="ntb-filter-type-icon" style={{ color: getTypeColor('Заказ') }}>
                                                        📋
                                                    </span>
                                                    Заказы
                                                </span>
                                            </label>
                                            <span className="ntb-filter-count">
                                                {visibleStats.orders}/{typeStats.orders}
                                            </span>
                                        </div>

                                        <div className="ntb-filter-checkbox-item">
                                            <label className="ntb-filter-checkbox-label">
                                                <input
                                                    type="checkbox"
                                                    checked={filters.operations}
                                                    onChange={() => handleFilterToggle('operations')}
                                                    className="ntb-filter-checkbox-input"
                                                />
                                                <span className={`ntb-filter-checkbox-custom ${filters.operations ? 'checked' : ''}`}>
                                                    {filters.operations ? <CheckBoxIcon /> : <CheckBoxOutlineBlankIcon />}
                                                </span>
                                                <span className="ntb-filter-checkbox-text">
                                                    <span className="ntb-filter-type-icon" style={{ color: getTypeColor('Операция') }}>
                                                        ⚙️
                                                    </span>
                                                    Операции
                                                </span>
                                            </label>
                                            <span className="ntb-filter-count">
                                                {visibleStats.operations}/{typeStats.operations}
                                            </span>
                                        </div>

                                        <div className="ntb-filter-checkbox-item">
                                            <label className="ntb-filter-checkbox-label">
                                                <input
                                                    type="checkbox"
                                                    checked={filters.resources}
                                                    onChange={() => handleFilterToggle('resources')}
                                                    className="ntb-filter-checkbox-input"
                                                />
                                                <span className={`ntb-filter-checkbox-custom ${filters.resources ? 'checked' : ''}`}>
                                                    {filters.resources ? <CheckBoxIcon /> : <CheckBoxOutlineBlankIcon />}
                                                </span>
                                                <span className="ntb-filter-checkbox-text">
                                                    <span className="ntb-filter-type-icon" style={{ color: getTypeColor('Ресурс') }}>
                                                        👤
                                                    </span>
                                                    Ресурсы
                                                </span>
                                            </label>
                                            <span className="ntb-filter-count">
                                                {visibleStats.resources}/{typeStats.resources}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div className="ntb-filter-hierarchy-info">
                                    <p className="ntb-filter-info-text">
                                        <strong>Внимание:</strong>
                                        <ul>
                                            <li>Заказы всегда отображаются</li>
                                            <li>Операции отображаются только у видимых заказов</li>
                                            <li>Ресурсы отображаются только у видимых операций</li>
                                        </ul>
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="ntb-filter-panel-actions">
                            <button
                                className="ntb-filter-action-btn ntb-secondary"
                                onClick={handleResetFilters}
                            >
                                <ClearIcon />
                                Сбросить всё
                            </button>
                            <button
                                className="ntb-filter-action-btn ntb-primary"
                                onClick={() => setShowFilterPanel(false)}
                            >
                                Применить
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Шапка таблицы */}
            <div className="ntb-table-header">
                <div className="ntb-table-header-top">
                    <div className="ntb-table-title-section">
                        <h1 className="ntb-table-title">Таблица элементов</h1>
                        <p className="ntb-table-subtitle">
                            Иерархическое представление заказов, операций и ресурсов
                        </p>
                    </div>

                    <div className="ntb-table-header-controls">
                        <button
                            className="ntb-table-filter-btn"
                            onClick={() => setShowFilterPanel(true)}
                        >
                            <FilterListIcon />
                            Фильтры
                            {(!filters.operations || !filters.resources) && (
                                <span className="ntb-filter-indicator"></span>
                            )}
                        </button>

                        <div className="ntb-table-search">
                            <input
                                type="text"
                                placeholder="Поиск по названию, номеру, описанию..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="ntb-table-search-input"
                            />
                            <span className="ntb-table-search-icon">🔍</span>
                            {searchTerm && (
                                <button
                                    className="ntb-table-search-clear"
                                    onClick={() => setSearchTerm('')}
                                >
                                    <ClearIcon />
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                <div className="ntb-table-header-stats">
                    <div className="ntb-stat-item">
                        <span className="ntb-stat-value">{visibleStats.orders}</span>
                        <span className="ntb-stat-label">Заказов</span>
                        <span className="ntb-stat-filter-info">
                            {!filters.orders ? 'скрыты' : ''}
                        </span>
                    </div>
                    <div className="ntb-stat-item">
                        <span className="ntb-stat-value">{visibleStats.operations}</span>
                        <span className="ntb-stat-label">Операций</span>
                        <span className="ntb-stat-filter-info">
                            {!filters.operations ? 'скрыты' : ''}
                        </span>
                    </div>
                    <div className="ntb-stat-item">
                        <span className="ntb-stat-value">{visibleStats.resources}</span>
                        <span className="ntb-stat-label">Ресурсов</span>
                        <span className="ntb-stat-filter-info">
                            {!filters.resources ? 'скрыты' : ''}
                        </span>
                    </div>
                    <div className="ntb-stat-item">
                        <span className="ntb-stat-value">{sortedData.length}</span>
                        <span className="ntb-stat-label">Всего</span>
                    </div>
                </div>

                {/* Индикаторы активных фильтров */}
                <div className="ntb-active-filters">
                    {searchTerm && (
                        <span className="ntb-active-filter-tag">
                            Поиск: "{searchTerm}"
                            <button
                                className="ntb-active-filter-remove"
                                onClick={() => setSearchTerm('')}
                            >
                                <ClearIcon />
                            </button>
                        </span>
                    )}
                    {!filters.operations && (
                        <span className="ntb-active-filter-tag">
                            Операции скрыты
                            <button
                                className="ntb-active-filter-remove"
                                onClick={() => handleFilterToggle('operations')}
                            >
                                <ClearIcon />
                            </button>
                        </span>
                    )}
                    {!filters.resources && (
                        <span className="ntb-active-filter-tag">
                            Ресурсы скрыты
                            <button
                                className="ntb-active-filter-remove"
                                onClick={() => handleFilterToggle('resources')}
                            >
                                <ClearIcon />
                            </button>
                        </span>
                    )}
                </div>
            </div>

            {/* Таблица */}
            <div className="ntb-table-wrapper">
                <table className="ntb-data-table">
                    <thead>
                    <tr>
                        <th className="ntb-checkbox-col">
                            <input
                                type="checkbox"
                                checked={selectedRows.length === sortedData.length && sortedData.length > 0}
                                onChange={handleSelectAll}
                                className="ntb-table-checkbox"
                            />
                        </th>
                        <th onClick={() => handleSort('type')}>
                            <div className="ntb-table-header-cell">
                                Тип
                                {sortConfig.key === 'type' && (
                                    <span className="ntb-sort-icon">
                                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                        </span>
                                )}
                            </div>
                        </th>
                        <th onClick={() => handleSort('name')}>
                            <div className="ntb-table-header-cell">
                                Название / Номер
                                {sortConfig.key === 'name' && (
                                    <span className="ntb-sort-icon">
                                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                        </span>
                                )}
                            </div>
                        </th>
                        <th onClick={() => handleSort('startDate')}>
                            <div className="ntb-table-header-cell">
                                Дата начала
                                {sortConfig.key === 'startDate' && (
                                    <span className="ntb-sort-icon">
                                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                        </span>
                                )}
                            </div>
                        </th>
                        <th onClick={() => handleSort('endDate')}>
                            <div className="ntb-table-header-cell">
                                Дата окончания
                                {sortConfig.key === 'endDate' && (
                                    <span className="ntb-sort-icon">
                                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                        </span>
                                )}
                            </div>
                        </th>
                        <th onClick={() => handleSort('status')}>
                            <div className="ntb-table-header-cell">
                                Статус
                                {sortConfig.key === 'status' && (
                                    <span className="ntb-sort-icon">
                                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                        </span>
                                )}
                            </div>
                        </th>
                        <th>
                            <div className="ntb-table-header-cell">
                                Описание
                            </div>
                        </th>
                    </tr>
                    </thead>
                    <tbody>
                    {sortedData.length === 0 ? (
                        <tr>
                            <td colSpan="7" className="ntb-table-empty">
                                <div className="ntb-table-empty-content">
                                    <div className="ntb-table-empty-icon">📊</div>
                                    <div className="ntb-table-empty-text">
                                        <h3>Нет данных для отображения</h3>
                                        <p>
                                            {searchTerm ? 'Попробуйте изменить поисковый запрос' :
                                                !filters.operations || !filters.resources ? 'Включите отображение операций или ресурсов' :
                                                    'Данные не найдены'}
                                        </p>
                                        <button
                                            className="ntb-table-empty-action"
                                            onClick={handleResetFilters}
                                        >
                                            Сбросить фильтры
                                        </button>
                                    </div>
                                </div>
                            </td>
                        </tr>
                    ) : (
                        sortedData.map((item) => {
                            const statusInfo = getStatusInfo(item.status);
                            const isSelected = selectedRows.includes(item.id);

                            return (
                                <tr
                                    key={item.id}
                                    className={`ntb-table-row ${isSelected ? 'ntb-selected' : ''} ntb-level-${item.level} ntb-type-${item.type.toLowerCase()}`}
                                >
                                    <td className="ntb-checkbox-col">
                                        <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={() => handleRowSelect(item.id)}
                                            className="ntb-table-checkbox"
                                        />
                                    </td>
                                    <td className="ntb-type-col">
                                        <div className="ntb-type-info">
                                                <span
                                                    className="ntb-type-icon"
                                                    style={{
                                                        backgroundColor: `${getTypeColor(item.type)}15`,
                                                        color: getTypeColor(item.type)
                                                    }}
                                                >
                                                    {getTypeIcon(item.type)}
                                                </span>
                                            <span className="ntb-type-text">{item.type}</span>
                                        </div>
                                    </td>
                                    <td className="ntb-name-col">
                                        <div className="ntb-name-info" style={{ paddingLeft: `${item.level * 24}px` }}>
                                            {item.level > 0 && (
                                                <div className="ntb-name-connector"></div>
                                            )}
                                            <div className="ntb-name-main">{item.name || 'Без названия'}</div>
                                            {item.number && (
                                                <div className="ntb-name-sub">{item.number}</div>
                                            )}
                                        </div>
                                    </td>
                                    <td className="ntb-date-col">
                                        {formatDate(item.startDate)}
                                    </td>
                                    <td className="ntb-date-col">
                                        {formatDate(item.endDate)}
                                    </td>
                                    <td className="ntb-status-col">
                                            <span
                                                className="ntb-status-badge"
                                                style={{
                                                    backgroundColor: statusInfo.bgColor,
                                                    color: statusInfo.color
                                                }}
                                            >
                                                {statusInfo.text}
                                            </span>
                                    </td>
                                    <td className="ntb-description-col">
                                        <div className="ntb-description-text" title={item.description}>
                                            {item.description || '—'}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })
                    )}
                    </tbody>
                </table>
            </div>

            {/* Футер таблицы */}
            <div className="ntb-table-footer">
                <div className="ntb-footer-info">
                    <span className="ntb-footer-text">
                        Показано: {sortedData.length} из {allItems.length} элементов
                    </span>
                    {selectedRows.length > 0 && (
                        <span className="ntb-footer-selected">
                            Выбрано: {selectedRows.length} элементов
                        </span>
                    )}
                </div>

                <div className="ntb-footer-actions">
                    <button
                        className="ntb-footer-action-btn"
                        onClick={() => {
                            console.log('Экспорт выбранных:', selectedRows);
                            alert(`Экспортировано ${selectedRows.length} элементов`);
                        }}
                        disabled={selectedRows.length === 0}
                    >
                        📥 Экспорт выбранных
                    </button>
                    <button
                        className="ntb-footer-action-btn ntb-primary"
                        onClick={() => {
                            console.log('Действие с выбранными:', selectedRows);
                            alert(`Выполняется действие с ${selectedRows.length} элементами`);
                        }}
                        disabled={selectedRows.length === 0}
                    >
                        🚀 Выполнить действие
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Table;